import { FC, useEffect, useState } from 'react';

import { shopOrderSellerRequestPendingCountUrl } from '@shared/api/shopOrder/shopOrder.hook';
import { IconFormer } from '@shared/components/IconsFormer';
import { Sidebar } from '@shared/components/Sidebar';
import { api_ } from '@shared/plugIn/axios';
import { useAuth } from '@shared/state/hooks/useAuth';

// const { Delivery, Flower, Modify, Receipt, Home, Question, Setting } = Icon();

//* 면세계산서/계좌환불 처리 대기 건수 — _id만 select하는 초경량 조회라 사실상 count 비용
interface PendingCounts {
  taxInvoiceRequest: number;
  manualRefund: number;
}

const navigation = (shopId: string, isAdmin: boolean, pending: PendingCounts) => [
  {
    name: 'Home',
    href: `/flower-seller/shop/${shopId}`,
    icon: IconFormer.Home,
    active: true,
  },
  {
    name: '구매시도자',
    href: `/flower-seller/shop/${shopId}/guest-order-manage`,
    icon: IconFormer.Loading,
    active: true,
  },
  {
    name: '주문배송관리',
    href: `/flower-seller/shop/${shopId}/delivery-manage`,
    icon: IconFormer.DeliveryBlue,
    active: true,
  },
  {
    name: '과거배송검색',
    href: `/flower-seller/shop/${shopId}/past-delivery`,
    icon: IconFormer.MagnifyingGlassRegular,
    active: true,
  },

  {
    name: '부고조회',
    href: `/flower-seller/shop/${shopId}/bugo-search`,
    icon: IconFormer.DPBlue,
    active: true,
  },
  {
    name: '입금대기',
    href: `/flower-seller/shop/${shopId}/waiting-list`,
    icon: IconFormer.Loading,
    active: true,
  },
  {
    // 남긴 CS 응대 메모 전체 목록 (자기 매장 범위는 서버가 걸러 준다)
    name: 'CS 응대 로그',
    href: `/flower-seller/shop/${shopId}/cs-memo-log`,
    icon: IconFormer.View,
    active: true,
  },
  {
    name: `면세계산서요청${
      pending.taxInvoiceRequest > 0 ? ` (${pending.taxInvoiceRequest})` : ''
    }`,
    href: `/flower-seller/shop/${shopId}/tax-invoice-requests`,
    icon: IconFormer.Receipt,
    active: true,
  },
  {
    name: `계좌환불요청${pending.manualRefund > 0 ? ` (${pending.manualRefund})` : ''}`,
    href: `/flower-seller/shop/${shopId}/manual-refund-requests`,
    icon: IconFormer.ModifyBlue,
    active: true,
  },
  {
    name: isAdmin ? '도매상품메뉴관리' : '상품메뉴관리',
    href: `/flower-seller/shop/${shopId}/shop-item-info`,
    icon: IconFormer.Flower,
    active: true,
  },
  {
    name: '소매상품메뉴관리',
    href: `/flower-seller/shop/${shopId}/shop-item`,
    icon: IconFormer.Flower,
    active: isAdmin,
  },
  {
    name: '정산',
    href: `/flower-seller/shop/${shopId}/account`,
    icon: IconFormer.ModifyBlue,
    active: true,
  },
  {
    name: '관리자설정',
    href: `/flower-seller/shop/${shopId}/account-settings`,
    icon: IconFormer.Setting,
    active: isAdmin,
  },
  {
    name: '판매자 정보',
    href: `/flower-seller/shop/${shopId}/info`,
    icon: IconFormer.UserBlue,
    active: true,
  },
];
const secondaryNavigation = (shopId: string) => [
  {
    name: '계정정보',
    href: `/flower-seller/shop/${shopId}/settings`,
    icon: IconFormer.Setting,
  },
];

interface Props {
  shopId: string;
}

export const SellerSidebar: FC<Props> = function SellerSidebar({ shopId }: Props) {
  const { isAdmin, sellerRole } = useAuth();

  //* 처리 대기 건수 배지 — 사이드바 마운트 시 1회 조회. seller는 owningFilter로 자기
  //* shop만, admin은 전체 대기열 건수. 구서버/실패면 조용히 0 (배지 없음).
  const [pending, setPending] = useState<PendingCounts>({
    taxInvoiceRequest: 0,
    manualRefund: 0,
  });
  //* role 문자열(안정적 enum 값)에만 의존. sellerRole 함수는 매 렌더 새 참조라
  //* 의존성에 두면 setPending 리렌더 → effect 재실행 → 재조회 무한루프가 됨.
  const role = sellerRole();
  useEffect(() => {
    let alive = true;
    (['taxInvoiceRequest', 'manualRefund'] as const).forEach((kind) => {
      api_
        .get<{ _id: string }[]>(shopOrderSellerRequestPendingCountUrl(kind, role))
        .then(({ data }) => {
          if (alive && Array.isArray(data)) {
            setPending((prev) => ({ ...prev, [kind]: data.length }));
          }
        })
        .catch(() => undefined);
    });
    return () => {
      alive = false;
    };
  }, [role]);

  return (
    <Sidebar
      navigation={navigation(shopId, isAdmin(), pending)}
      secondaryNavigation={secondaryNavigation(shopId)}
    />
  );
};
